import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GUIDES } from "@/data/guidesData";
import { LOGO_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Clock } from "lucide-react";

const SITE = "https://creatara.ai";

export default function GuidesIndexPage() {
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: GUIDES.map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/guides/${g.slug}`,
      name: g.title,
    })),
  };

  return (
    <>
      <Helmet>
        <title>Guides: AI Content, Monetisation & GEO for Solopreneurs | Creatara</title>
        <meta name="description" content="In-depth guides for solopreneurs: how to monetise AI content, get cited by ChatGPT and Perplexity, schedule across platforms, and run affiliate marketing with AI." />
        <link rel="canonical" href={`${SITE}/guides`} />
        <meta property="og:title" content="Solopreneur Guides — AI Content, Monetisation, GEO | Creatara" />
        <meta property="og:description" content="In-depth guides for solopreneurs monetising AI content." />
        <meta property="og:url" content={`${SITE}/guides`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="font-bold text-lg">Creatara</span>
            </Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700">Try Free</Button></Link>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-6 pt-32 pb-20" data-testid="guides-index">
          <div className="mb-12">
            <Badge variant="secondary" className="mb-3"><BookOpen className="w-3 h-3 mr-1" /> Guides</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Solopreneur Guides</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">In-depth playbooks for AI content monetisation, GEO citation tracking, scheduling, and affiliate marketing — written by the Creatara team.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {GUIDES.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="group rounded-xl bg-white/5 border border-white/10 hover:border-violet-500/40 transition-all p-6 flex flex-col" data-testid={`guide-card-${g.slug}`}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Clock className="w-3 h-3" /> {g.readMinutes} min · {new Date(g.publishedAt).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <h2 className="text-xl font-bold mb-2 group-hover:text-violet-400 transition-colors">{g.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{g.metaDescription}</p>
                <p className="text-xs text-violet-400 mt-4 font-semibold">Read guide →</p>
              </Link>
            ))}
          </div>

          <div className="mt-16 rounded-xl bg-gradient-to-br from-violet-600/20 to-amber-600/10 border border-violet-500/30 p-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Want the tool these guides describe?</h2>
            <p className="text-muted-foreground mb-5">AI content generation + scheduling + affiliate tracking + GEO citation tracker, all in one £29/month subscription.</p>
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700">Try Creatara free →</Button></Link>
          </div>
        </div>
      </div>
    </>
  );
}
